import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useGameStore, selectCanCashOut, selectPotentialWin } from '@stores/gameStore';
import { useRGS } from '@hooks/useRGS';
import { formatMinorUnits } from '@/utils/format-amount';

/**
 * Cash out button component
 * Appears during playing phase when player has an active bet
 */
export function CashOutButton(): React.JSX.Element | null {
  const { gamePhase, currentBet, multiplier, currency, currencyDecimals } = useGameStore();
  const canCashOut = useGameStore(selectCanCashOut);
  const potentialWin = useGameStore(selectPotentialWin);
  const { cashOut } = useRGS();

  // Only show during playing phase with active bet
  const shouldShow = useMemo(() => {
    return gamePhase === 'playing' && currentBet !== null && !currentBet.cashedOut;
  }, [gamePhase, currentBet]);

  if (!shouldShow) {
    // Show cashed out confirmation
    if (gamePhase === 'playing' && currentBet?.cashedOut) {
      return (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="rounded-lg bg-aviator-success px-8 py-4 text-center shadow-lg"
        >
          <p className="text-lg font-bold text-white">CASHED OUT!</p>
          <p className="text-3xl font-bold text-white">
            ${formatMinorUnits(currentBet.amount * (currentBet.cashoutMultiplier ?? 1), currency, currencyDecimals ?? undefined)}
          </p>
          <p className="text-sm text-white/80">
            @ {currentBet.cashoutMultiplier?.toFixed(2)}x
          </p>
        </motion.div>
      );
    }

    // Show waiting message if no bet placed
    if (gamePhase === 'playing' && !currentBet) {
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-lg bg-aviator-skyDark/80 px-6 py-3 text-center"
        >
          <p className="text-sm text-white/70">No bet placed this round</p>
        </motion.div>
      );
    }

    return null;
  }

  const handleCashOut = (): void => {
    if (canCashOut) {
      cashOut();
    }
  };

  return (
    <motion.button
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleCashOut}
      disabled={!canCashOut}
      className={`rounded-lg px-12 py-6 text-center shadow-2xl transition-all ${
        canCashOut
          ? 'bg-gradient-to-r from-aviator-gold to-yellow-500 hover:from-yellow-400 hover:to-aviator-gold'
          : 'cursor-not-allowed bg-gray-600/50'
      }`}
      style={{
        boxShadow: canCashOut ? '0 0 30px rgba(251, 191, 36, 0.5)' : 'none',
      }}
    >
      <motion.div
        animate={canCashOut ? { scale: [1, 1.02, 1] } : {}}
        transition={{ repeat: Infinity, duration: 0.5 }}
      >
        <p className="text-2xl font-bold text-aviator-skyDark">CASH OUT</p>
        <p className="text-4xl font-bold text-aviator-skyDark">
          ${formatMinorUnits(potentialWin, currency, currencyDecimals ?? undefined)}
        </p>
        <p className="text-sm font-semibold text-aviator-skyDark/80">
          @ {multiplier.toFixed(2)}x
        </p>
      </motion.div>
    </motion.button>
  );
}
